import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/CtaBand";
import { PillButton } from "@/components/PillButton";
import { Icon } from "@/lib/icons";
import { IMG } from "@/lib/images";
import { Reveal } from "@/components/Reveal";
import { CardsSkeleton, ErrorState, EmptyState } from "@/components/States";
import { useProducts } from "@/hooks/useSiteData";

export default function ProductsPage() {
  const { data, isLoading, isError, refetch } = useProducts();
  return (
    <>
      <Seo title="Products" description="AI Workforce Scheduler, Digital Academe, RFID Walk-Through Attendance, Fleet & Asset Management and Cloud Hospitality Management - enterprise products by Jonaren Technology." path="/products" />
      <PageHero eyebrow="Our products" title="Enterprise products, born from real client problems." lead="Configurable platforms that deploy in weeks - on cloud, private cloud or on-premise - and integrate with the systems you already run." crumbs={[{ label: "Products" }]} image={IMG.dashboard} testId="products-page-hero" />
      <section className="section paper">
        <div className="container-x space-y-6">
          {isLoading && <CardsSkeleton count={3} />}
          {isError && <ErrorState onRetry={refetch} />}
          {data && data.length === 0 && <EmptyState title="Products are being updated" />}
          {data?.map((p, i) => (
            <Reveal key={p.id}>
              <article className="grid overflow-hidden rounded-3xl bg-white lg:grid-cols-12" data-testid={`products-list-card-${p.slug}`}>
                <div className={`relative min-h-[280px] lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <img src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0e1013]/70 to-transparent p-6 text-white"><span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary"><Icon name={p.icon} className="h-3.5 w-3.5" />{p.featured ? "Flagship" : `Product ${String(i + 1).padStart(2, "0")}`}</span></div>
                </div>
                <div className="p-8 lg:col-span-6 lg:p-12">
                  <div className="flex flex-wrap gap-2">{p.industries.slice(0, 4).map((x) => <span key={x} className="rounded-full border border-border px-3 py-1 text-xs font-medium">{x}</span>)}</div>
                  <h2 className="mt-5 font-heading text-3xl font-medium leading-tight sm:text-4xl">{p.name}</h2>
                  <p className="mt-2 text-primary">{p.tagline}</p>
                  <p className="mt-4 text-muted-foreground">{p.description}</p>
                  <ul className="mt-6 grid gap-2 sm:grid-cols-2">{p.features.slice(0, 6).map((f) => <li key={f} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{f}</li>)}</ul>
                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <PillButton to={`/contact?interest=${encodeURIComponent(p.name)}`} variant="primary" testId={`products-list-demo-${p.slug}`}>Request a demo</PillButton>
                    <Link to={`/products/${p.slug}`} className="link-arrow self-center px-2" data-testid={`products-list-details-${p.slug}`}>Product details <ArrowUpRight /></Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
      <div className="paper"><CtaBand title="Need a product tailored to your workflow?" text="All JNT products can be customised and deployed on your preferred infrastructure. Tell us what you need." /></div>
    </>
  );
}
